import React, { useRef, useState, useEffect } from 'react';

/**
 * ConfigurationPreview - Aperçu visuel de la configuration de toile
 * Affiche un schéma avec les laizes, œillets, sanglons, etc.
 */
const ConfigurationPreview = ({
  largeur,
  projection,
  projection2,
  hauteurLambrequin,
  lambrequin,
  isDoublePente,
  isLambrequinSeul,
  isPergola,
  isBriseVue,
  optionsSupp,
  renfortLyreDetails,
  lestageDetails,
  nombreSanglons,
  fabricCalculation,
  // Props pour le marquage
  marquageText = '',
  marquageCalculation = null,
  marquageColor = 'white',
  // Props pour l'apparence du produit
  productImage = null,
  productRal = null
}) => {
  // Ref pour mesurer la hauteur du conteneur du lambrequin
  const lambrequinContainerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(0);

  // Convertir le code RAL en couleur CSS
  const getColorFromRal = (ral) => {
    // Mapping des codes RAL courants vers des couleurs CSS
    const ralColors = {
      '3003': '#9B111E', // Rouge rubis
      '3004': '#75151E', // Rouge pourpre
      '3020': '#CC0605', // Rouge signalisation
      '1015': '#E6D2B5', // Ivoire clair
      '1013': '#EAE6CA', // Blanc perlé
      '5015': '#2271B3', // Bleu ciel
      '6005': '#114232', // Vert mousse
      '7016': '#293133', // Gris anthracite
      '9005': '#0A0A0D', // Noir foncé
      '9010': '#F5F5F5', // Blanc pur
    };
    return ralColors[ral] || null;
  };

  // Déterminer le style de fond pour le lambrequin
  const getLambrequinBackground = () => {
    if (productImage) {
      return {
        backgroundImage: `url(${productImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'repeat'
      };
    }
    if (productRal) {
      const color = getColorFromRal(productRal);
      if (color) {
        return {
          background: `linear-gradient(to bottom, ${color}dd, ${color})`
        };
      }
    }
    // Couleur par défaut (orange/beige)
    return {
      background: 'linear-gradient(to bottom, rgb(254 243 199), rgb(253 186 116))'
    };
  };

  // Mesurer la hauteur du conteneur quand il est rendu
  useEffect(() => {
    const updateHeight = () => {
      if (lambrequinContainerRef.current) {
        const height = lambrequinContainerRef.current.offsetHeight;
        setContainerHeight(height);
      }
    };

    updateHeight();
    
    // Observer les changements de taille
    const resizeObserver = new ResizeObserver(updateHeight);
    if (lambrequinContainerRef.current) {
      resizeObserver.observe(lambrequinContainerRef.current);
    }
    
    return () => resizeObserver.disconnect();
  }, [largeur, hauteurLambrequin]);
  // Condition d'affichage du schéma
  const shouldShow = optionsSupp.renfortLyre || 
                     optionsSupp.textePersonnalise || 
                     optionsSupp.agrafes || 
                     optionsSupp.clips || 
                     optionsSupp.oeillets || 
                     isLambrequinSeul || 
                     isPergola || 
                     isBriseVue;

  if (!shouldShow) return null;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <h4 className="text-md font-medium mb-3 text-blue-900">Aperçu de la configuration :</h4>
      <div className="bg-white border-2 border-gray-300 rounded-lg p-4">
        {/* Schéma stylisé de la toile - Masqué pour lambrequin seul */}
        {!isLambrequinSeul && (() => {
          // Calculer le ratio réel basé sur les dimensions
          const avanceeCalcul = isDoublePente ? (projection + projection2) : projection;
          const ratioReel = (avanceeCalcul / largeur) * 100;
          // Limiter le ratio entre 25% et 100% pour un affichage optimal
          const ratioAffichage = Math.min(Math.max(ratioReel, 25), 100);
          
          return (
            <div className="relative w-full" style={{ paddingBottom: `${ratioAffichage}%` }}>
              {/* Toile de fond avec rayures */}
              <div className="absolute inset-0 bg-gradient-to-b from-orange-100 to-orange-200 rounded shadow-md overflow-hidden">
                {/* Rayures décoratives */}
                <div className="absolute inset-0" style={{
                  backgroundImage: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.1) 0px, rgba(255,255,255,0.1) 2px, transparent 2px, transparent 20px)',
                }}></div>
                
                {/* Jonc magnétique (ligne bleue en haut) */}
                {optionsSupp.textePersonnalise && (
                  <div className="absolute top-0 left-0 right-0 h-2 bg-blue-500 shadow-md" title="Jonc magnétique"></div>
                )}
                
                {/* Jonc à montage rapide (ligne verte en haut) */}
                {optionsSupp.agrafes && (
                  <div className="absolute top-0 left-0 right-0 h-2 bg-green-500 shadow-md" title="Jonc à montage rapide"></div>
                )}
                
                {/* Renforts de lyre */}
                {optionsSupp.renfortLyre && renfortLyreDetails?.positions?.map((position, index) => {
                  const percentagePosition = (position / largeur) * 100;
                  return (
                    <div
                      key={index}
                      className="absolute top-0 bottom-0 w-1 bg-red-600 shadow-lg"
                      style={{ left: `${percentagePosition}%` }}
                      title={`Renfort ${index + 1}: ${Math.round(position)} cm`}
                    >
                      {/* Marqueur visuel du renfort */}
                      <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-red-700 rounded-full border-2 border-white"></div>
                      <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-red-700 rounded-full border-2 border-white"></div>
                    </div>
                  );
                })}
                
                {/* Laizes (traits verticaux en pointillés) */}
                {((isPergola || isBriseVue) || optionsSupp.clips) && fabricCalculation && fabricCalculation.nombre_laizes && (() => {
                  const nombreLaizes = fabricCalculation.nombre_laizes;
                  const largeurLaize = 113; // cm
                  const largeurTotale = fabricCalculation.largeur_totale || largeur + 6;
                  const espaceTotal = nombreLaizes * largeurLaize;
                  const margeGauche = (largeurTotale - espaceTotal) / 2;
                  
                  // Générer les positions des bordures entre laizes
                  const positions = [];
                  for (let i = 0; i <= nombreLaizes; i++) {
                    positions.push(margeGauche + (i * largeurLaize));
                  }
                  
                  return positions.map((position, index) => {
                    const percentagePosition = (position / largeurTotale) * 100;
                    return (
                      <div
                        key={`laize-${index}`}
                        className="absolute top-0 bottom-0 w-px"
                        style={{ 
                          left: `${percentagePosition}%`,
                          borderLeft: '2px dashed rgba(156, 163, 175, 0.5)'
                        }}
                        title={`Laize ${index}: ${Math.round(position)} cm`}
                      ></div>
                    );
                  });
                })()}
                
                {/* Œillets inox - Uniquement pour pergola et brise-vue */}
                {(isPergola || isBriseVue) && (() => {
                  const oeillets = [];
                  const espacementCible = 25; // cm
                  const decalageBord = 3; // cm
                  
                  const nombreOeilletsLargeur = Math.max(2, Math.ceil(largeur / espacementCible));
                  const nombreOeilletsAvancee = Math.max(2, Math.ceil(projection / espacementCible));
                  
                  const decalageHorizontal = (decalageBord / largeur) * 100;
                  const decalageVertical = (decalageBord / projection) * 100;
                  
                  // Côté HAUT
                  for (let i = 0; i < nombreOeilletsLargeur; i++) {
                    const posX = decalageHorizontal + (i / (nombreOeilletsLargeur - 1)) * (100 - 2 * decalageHorizontal);
                    oeillets.push(
                      <div
                        key={`top-${i}`}
                        className="absolute w-1 h-1 bg-gray-800 rounded-full border border-white shadow-sm"
                        style={{ 
                          left: `${posX}%`, 
                          top: `${decalageVertical}%`,
                          transform: 'translate(-50%, -50%)'
                        }}
                        title="Œillet inox"
                      ></div>
                    );
                  }
                  
                  // Côté BAS
                  for (let i = 0; i < nombreOeilletsLargeur; i++) {
                    const posX = decalageHorizontal + (i / (nombreOeilletsLargeur - 1)) * (100 - 2 * decalageHorizontal);
                    oeillets.push(
                      <div
                        key={`bottom-${i}`}
                        className="absolute w-1 h-1 bg-gray-800 rounded-full border border-white shadow-sm"
                        style={{ 
                          left: `${posX}%`, 
                          top: `${100 - decalageVertical}%`,
                          transform: 'translate(-50%, -50%)'
                        }}
                        title="Œillet inox"
                      ></div>
                    );
                  }
                  
                  // Côté GAUCHE
                  for (let i = 1; i < nombreOeilletsAvancee - 1; i++) {
                    const posY = decalageVertical + (i / (nombreOeilletsAvancee - 1)) * (100 - 2 * decalageVertical);
                    oeillets.push(
                      <div
                        key={`left-${i}`}
                        className="absolute w-1 h-1 bg-gray-800 rounded-full border border-white shadow-sm"
                        style={{ 
                          left: `${decalageHorizontal}%`, 
                          top: `${posY}%`,
                          transform: 'translate(-50%, -50%)'
                        }}
                        title="Œillet inox"
                      ></div>
                    );
                  }
                  
                  // Côté DROIT
                  for (let i = 1; i < nombreOeilletsAvancee - 1; i++) {
                    const posY = decalageVertical + (i / (nombreOeilletsAvancee - 1)) * (100 - 2 * decalageVertical);
                    oeillets.push(
                      <div
                        key={`right-${i}`}
                        className="absolute w-1 h-1 bg-gray-800 rounded-full border border-white shadow-sm"
                        style={{ 
                          left: `${100 - decalageHorizontal}%`, 
                          top: `${posY}%`,
                          transform: 'translate(-50%, -50%)'
                        }}
                        title="Œillet inox"
                      ></div>
                    );
                  }
                  
                  return oeillets;
                })()}
                
                {/* Sanglons - Uniquement pour pergola avec option sanglons */}
                {isPergola && optionsSupp.sanglons && nombreSanglons > 0 && fabricCalculation && (() => {
                  const sanglons = [];
                  const longueurTrait = 10;
                  const epaisseurTrait = 2;
                  
                  const largeurTotale = fabricCalculation.largeur_totale || largeur + 12;
                  const avanceeTotale = fabricCalculation.avancee_totale || projection + 12;
                  
                  const largeurTraitPct = (longueurTrait / largeurTotale) * 100;
                  
                  // Trouver la meilleure répartition en grille
                  let meilleureLargeur = 1;
                  let meilleureAvancee = nombreSanglons;
                  let meilleureDifference = Math.abs(largeurTotale / meilleureLargeur - avanceeTotale / meilleureAvancee);
                  
                  for (let l = 1; l <= nombreSanglons; l++) {
                    if (nombreSanglons % l === 0) {
                      const a = nombreSanglons / l;
                      const ratioToile = largeurTotale / avanceeTotale;
                      const ratioGrille = l / a;
                      const difference = Math.abs(ratioToile - ratioGrille);
                      
                      if (difference < meilleureDifference) {
                        meilleureDifference = difference;
                        meilleureLargeur = l;
                        meilleureAvancee = a;
                      }
                    }
                  }
                  
                  const nombreSanglonsLargeur = meilleureLargeur;
                  const nombreSanglonsAvancee = meilleureAvancee;
                  
                  const espacementLargeur = largeurTotale / (nombreSanglonsLargeur + 1);
                  const espacementAvancee = avanceeTotale / (nombreSanglonsAvancee + 1);
                  
                  for (let i = 0; i < nombreSanglonsLargeur; i++) {
                    for (let j = 0; j < nombreSanglonsAvancee; j++) {
                      const posAbsolueLargeur = (i + 1) * espacementLargeur;
                      const posAbsolueAvancee = (j + 1) * espacementAvancee;
                      
                      const posX = (posAbsolueLargeur / largeurTotale) * 100;
                      const posY = (posAbsolueAvancee / avanceeTotale) * 100;
                      
                      sanglons.push(
                        <div
                          key={`sanglon-${i}-${j}`}
                          className="absolute bg-blue-600"
                          style={{ 
                            top: `${posY}%`,
                            left: `${posX - largeurTraitPct/2}%`,
                            width: `${largeurTraitPct}%`,
                            height: `${epaisseurTrait}px`,
                            transform: 'translateY(-50%)'
                          }}
                          title={`Sanglon (${Math.round(posAbsolueLargeur)}cm, ${Math.round(posAbsolueAvancee)}cm)`}
                        ></div>
                      );
                    }
                  }
                  
                  return sanglons;
                })()}
                
                {/* Labels des dimensions */}
                <div className="absolute -top-6 left-0 right-0 text-center text-xs font-medium text-gray-600">
                  Largeur: {largeur} cm
                </div>
              </div>
            </div>
          );
        })()}
        
        {/* Lambrequin avec fourreau */}
        {((optionsSupp.oeillets && lambrequin !== 'sans') || isLambrequinSeul) && (
          <div 
            ref={lambrequinContainerRef}
            className="relative w-full mt-2" 
            style={{ 
              paddingBottom: `${(hauteurLambrequin / largeur) * 100}%`, 
              minHeight: '30px'
            }}
          >
            <div 
              className="absolute inset-0 rounded shadow-md overflow-hidden"
              style={getLambrequinBackground()}
            >
              {/* Rayures décoratives (overlay subtil) */}
              <div className="absolute inset-0" style={{
                backgroundImage: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 2px, transparent 2px, transparent 20px)',
              }}></div>
              
              {/* Texte de marquage - 80% de la hauteur du lambrequin */}
              {marquageText && marquageCalculation && containerHeight > 0 && (
                <div 
                  className="absolute inset-0 flex items-center justify-center overflow-hidden"
                  title={`Texte: "${marquageText}" - ${marquageCalculation.hauteur_texte_cm}cm × ${marquageCalculation.largeur_texte_cm}cm`}
                >
                  <span
                    style={{
                      fontFamily: 'Arial Black, Arial Bold, Arial, sans-serif',
                      fontWeight: 900,
                      // 80% de la hauteur réelle du conteneur en pixels
                      fontSize: `${containerHeight * 0.8}px`,
                      lineHeight: 1,
                      // Couleur du texte selon le choix de l'utilisateur
                      color: marquageColor === 'white' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
                      textShadow: marquageColor === 'white' 
                        ? '1px 1px 3px rgba(0,0,0,0.5), -1px -1px 3px rgba(0,0,0,0.3)' 
                        : '1px 1px 2px rgba(255,255,255,0.5)',
                      whiteSpace: 'nowrap',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {marquageText}
                  </span>
                </div>
              )}
              
              {/* Laizes pour lambrequin seul */}
              {isLambrequinSeul && optionsSupp.clips && fabricCalculation && fabricCalculation.nombre_laizes && (() => {
                const nombreLaizes = fabricCalculation.nombre_laizes;
                const largeurLaize = 113;
                const largeurTotale = fabricCalculation.largeur_totale || largeur + 6;
                const espaceTotal = nombreLaizes * largeurLaize;
                const margeGauche = (largeurTotale - espaceTotal) / 2;
                
                const positions = [];
                for (let i = 0; i <= nombreLaizes; i++) {
                  positions.push(margeGauche + (i * largeurLaize));
                }
                
                return positions.map((position, index) => {
                  const percentagePosition = (position / largeurTotale) * 100;
                  return (
                    <div
                      key={`laize-lambrequin-${index}`}
                      className="absolute top-0 bottom-0 w-px"
                      style={{ 
                        left: `${percentagePosition}%`,
                        borderLeft: '2px dashed rgba(156, 163, 175, 0.5)'
                      }}
                      title={`Laize ${index}: ${Math.round(position)} cm`}
                    ></div>
                  );
                });
              })()}
              
              {/* Fourreau jaune en bas */}
              {optionsSupp.oeillets && lestageDetails && (
                <div className="absolute bottom-0 left-0 right-0 h-3 bg-yellow-400 shadow-md" title={`Fourreau pour tube Ø${lestageDetails.diametreTube}mm - ${lestageDetails.nombreLests} lests`}></div>
              )}
            </div>
            
            {/* Label du lambrequin */}
            <div className="absolute -bottom-5 left-0 right-0 text-center text-xs font-medium text-gray-600">
              Lambrequin: {hauteurLambrequin} cm
            </div>
          </div>
        )}
        
        {/* Légende */}
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-gray-600">
          {!isLambrequinSeul && (
            <div className="flex items-center gap-2">
              <div 
                className="w-4 h-4 border border-gray-300 rounded"
                style={productRal ? { backgroundColor: getColorFromRal(productRal) || '#fed7aa' } : { backgroundColor: '#fed7aa' }}
              ></div>
              <span>Toile</span>
            </div>
          )}
          {isLambrequinSeul && (
            <div className="flex items-center gap-2">
              <div 
                className="w-4 h-4 border border-gray-300 rounded"
                style={productRal ? { backgroundColor: getColorFromRal(productRal) || '#fed7aa' } : { backgroundColor: '#fed7aa' }}
              ></div>
              <span>Lambrequin</span>
            </div>
          )}
          {optionsSupp.renfortLyre && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-1 bg-red-600 rounded"></div>
              <span>Renfort de lyre</span>
            </div>
          )}
          {optionsSupp.textePersonnalise && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-1 bg-blue-500 rounded"></div>
              <span>Jonc magnétique</span>
            </div>
          )}
          {optionsSupp.agrafes && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-1 bg-green-500 rounded"></div>
              <span>Jonc à montage rapide</span>
            </div>
          )}
          {optionsSupp.oeillets && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-1 bg-yellow-400 rounded"></div>
              <span>Fourreau/Lestage</span>
            </div>
          )}
          {(isPergola || isBriseVue) && (
            <div className="flex items-center gap-2">
              <div className="w-1 h-1 bg-gray-800 rounded-full border border-white"></div>
              <span>Œillets inox</span>
            </div>
          )}
          {isPergola && optionsSupp.sanglons && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-1 bg-blue-600 rounded"></div>
              <span>Sanglons</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPreview;
